// Voice library (pages/models.tsx, components/modals/ModelSearchPageV2) and
// the category hubs (/ai-voices/[slug], components/hub/CategoryHub).
// See messages/README.md for how to add strings.
//
// Category variables, filled by utils/categoryNames.ts:
// - {category}: English title-cased badge ("Public Figure"); es/pt the name
//   as it reads mid-sentence ("famosos").
// - {categoryLower}: English lower-cased; es/pt same as {category}.
// - {of}: es/pt only, "de famosos" / "en japonés" (languages take en/em).
// library.cat.<slug> / library.lang.<slug> name a badge by its URL slug;
// badges without one (mostly show and brand names) keep their own name.
export const library = {
  en: {
    "library.of": "{name}",
    "library.ofLanguage": "{name}",

    // SEO (pages/models.tsx and ModelSearchPageV2)
    "library.seo.title": "AI Voice Library{filter} | Jammable",
    "library.seo.titleFiltered": "{filter} AI Voice Library | Jammable",
    "library.seo.description":
      "Browse 60,000+ AI voice models. Create AI covers with voices from anime, gaming, cartoon, K-pop, and more. New voices added daily.",
    "library.seo.ogDescription":
      "Browse 60,000+ AI voice models. Create AI covers with voices from anime, gaming, cartoon, K-pop, and more.",
    "library.seo.listDescription":
      "Browse 60,000+ AI voice models. Create AI covers with voices from anime, gaming, cartoon, K-pop, and more. New voices added daily.",
    "library.seo.listDescriptionFiltered":
      "{filter} AI voices on Jammable. Pick a voice and make an AI cover or text-to-speech with it. 60,000+ voices in the library.",
    "library.crumb.home": "Home",
    "library.crumb.library": "AI Voice Library",
    "library.crumb.filter": "{category} Voices",

    // ModelSearchPageV2
    "library.title": "Voice library",
    "library.subtitle":
      "60,000+ voices. Tap one to hear it, or make a cover straight away.",
    "library.train": "Train your own voice",
    "library.search": "Search voices",
    "library.searchIn": "Search {categoryLower} voices",
    "library.categoriesNav": "Voice categories",
    "library.all": "All",
    "library.moreCategories": "More categories",
    "library.fewerCategories": "Fewer categories",
    "library.collections": "Collections",
    "library.collections.prev": "Previous collections",
    "library.collections.next": "More collections",
    "library.collection.voices": "{n} voices",
    "library.voicesIn": "{category} voices",
    "library.allVoices": "All voices",
    "library.empty.title": "No voices found",
    "library.empty.body": "Try another name, or train the voice yourself.",
    "library.loadMore": "Load more voices",

    // components/voices/VoiceSortTabs
    "library.sort.aria": "Sort voices",
    "library.sort.hot": "Trending today",
    "library.sort.hot.short": "Trending",
    "library.sort.top": "Most used",
    "library.sort.top.short": "Most used",
    "library.sort.liked": "Most liked",
    "library.sort.liked.short": "Most liked",
    "library.sort.new": "Newest",
    "library.sort.new.short": "Newest",

    // CategoryHub
    "library.hub.title":
      "{category} AI Voices — Make AI Covers With {category} Voices | Jammable",
    "library.hub.titlePage": "{category} AI Voices, Page {page} | Jammable",
    "library.hub.description":
      "Browse {count}+ {category} AI voices on Jammable. Pick any voice to make an AI cover or use the AI voice changer.",
    "library.hub.descriptionPage":
      "Page {page} of {lastPage} of {category} AI voices on Jammable, ranked by uses. Pick any voice to make an AI cover or use the AI voice changer.",
    "library.hub.name": "{category} AI Voices",
    "library.hub.crumbVoices": "AI Voices",
    "library.hub.voices": "Voices",
    "library.hub.page": "Page {page}",
    "library.hub.h1": "{category} AI voices",
    "library.hub.intro":
      "Browse {count}+ {category} AI voices. Pick any one to make an AI cover, or use it as a voice changer.",
    "library.hub.popular": "Popular {categoryLower} voices",
    "library.hub.mostUsedPage":
      "Most used {categoryLower} voices, page {page} of {lastPage}",
    "library.hub.noMatch": "No {categoryLower} voices match that search.",
    "library.hub.openLibrary": "Open in the voice library",
    "library.hub.moreCategories": "Explore more categories",
    "library.hub.faq": "Frequently asked questions",
    "library.hub.keepExploring": "Keep exploring",
    "library.hub.moreWays": "More ways to create with Jammable.",
    "library.hub.link.aiCover": "AI Cover Generator",
    "library.hub.link.voiceChanger": "AI Voice Changer",
    "library.hub.link.allVoices": "Browse all AI voices",
    "library.hub.pages": "Pages",
    "library.hub.first": "First",
    "library.hub.prev": "Previous",
    "library.hub.next": "Next",
    "library.hub.last": "Last",
    "library.hub.faq1.q": "What are {category} AI voices?",
    "library.hub.faq1.a":
      "{category} AI voices are voice models in the {categoryLower} category that you can use to make AI covers or change your own voice. Jammable has {count}+ of them, with new voices added every day.",
    "library.hub.faq2.q": "How do I use a {category} AI voice?",
    "library.hub.faq2.a":
      "Open any {category} voice, add audio you have the rights to (or record your own), and press generate. Jammable converts the vocals into that voice and returns a finished cover in seconds.",
    "library.hub.faq3.q": "How many {category} voices are there?",
    "library.hub.faq3.a":
      "Jammable currently has {count}+ {category} AI voices, and the library grows daily as the community uploads and trains new ones.",

    // Category names. English shows the badge itself; these document the keys.
    "library.cat.public-figure": "Public Figure",
    "library.cat.music": "Music",
    "library.cat.cartoon": "Cartoon",
    "library.cat.gaming": "Gaming",
    "library.cat.fictional": "Fictional",
    "library.cat.anime": "Anime",
    "library.cat.influencer": "Influencer",
    "library.cat.singer": "Singer",
    "library.cat.comedy": "Comedy",
    "library.cat.pop": "Pop",
    "library.cat.youtuber": "Youtuber",
    "library.cat.tv-show": "Tv Show",
    "library.cat.rap": "Rap",
    "library.cat.fictional-character": "Fictional Character",
    "library.cat.movie": "Movie",
    "library.cat.movies": "Movies",
    "library.cat.funny": "Funny",
    "library.cat.kpop": "Kpop",
    "library.cat.k-pop": "K-pop",
    "library.cat.parody": "Parody",
    "library.cat.animation": "Animation",
    "library.cat.animated": "Animated",
    "library.cat.rock": "Rock",
    "library.cat.vtuber": "Vtuber",
    "library.cat.fantasy": "Fantasy",
    "library.cat.game": "Game",
    "library.cat.entertainment": "Entertainment",
    "library.cat.rapper": "Rapper",
    "library.cat.horror": "Horror",
    "library.cat.actor": "Actor",
    "library.cat.actors": "Actors",
    "library.cat.actress": "Actress",
    "library.cat.meme": "Meme",
    "library.cat.action": "Action",
    "library.cat.educational": "Educational",
    "library.cat.hip-hop-rap": "Hip-hop/rap",
    "library.cat.hip-hop": "Hip-hop",
    "library.cat.adventure": "Adventure",
    "library.cat.tv": "Tv",
    "library.cat.female": "Female",
    "library.cat.male": "Male",
    "library.cat.superhero": "Superhero",
    "library.cat.drama": "Drama",
    "library.cat.sci-fi": "Sci-fi",
    "library.cat.science-fiction": "Science Fiction",
    "library.cat.alternative": "Alternative",
    "library.cat.metal": "Metal",
    "library.cat.technology": "Technology",
    "library.cat.indie": "Indie",
    "library.cat.celebrity": "Celebrity",
    "library.cat.fiction": "Fiction",
    "library.cat.american": "American",
    "library.cat.british": "British",
    "library.cat.australian": "Australian",
    "library.cat.latino": "Latino",
    "library.cat.mexico": "Mexico",
    "library.cat.villain": "Villain",
    "library.cat.politics": "Politics",
    "library.cat.president": "President",
    "library.cat.animal": "Animal",
    "library.cat.animals": "Animals",
    "library.cat.dog": "Dog",
    "library.cat.cat": "Cat",
    "library.cat.sports": "Sports",
    "library.cat.wrestling": "Wrestling",
    "library.cat.streamer": "Streamer",
    "library.cat.country": "Country",
    "library.cat.robot": "Robot",
    "library.cat.sound-effects": "Sound Effects",
    "library.cat.historical": "Historical",
    "library.cat.folk": "Folk",
    "library.cat.r-b": "R&b",
    "library.cat.rnb": "Rnb",
    "library.cat.jazz": "Jazz",
    "library.cat.reggaeton": "Reggaeton",
    "library.cat.electronic": "Electronic",
    "library.cat.pop-music": "Pop Music",
    "library.cat.comic": "Comic",
    "library.cat.demon": "Demon",
    "library.cat.news": "News",
    "library.cat.artist": "Artist",
    "library.cat.artistas": "Artistas",
    "library.cat.cantantes": "Cantantes",
    "library.cat.cantante": "Cantante",
    "library.cat.personajes": "Personajes",
    "library.cat.character": "Character",
    "library.cat.singing": "Singing",
    "library.cat.mystery": "Mystery",
    "library.cat.christmas": "Christmas",
    "library.cat.sitcom": "Sitcom",
    "library.cat.pirate": "Pirate",
    "library.cat.toy": "Toy",
    "library.cat.classic": "Classic",
    "library.cat.art": "Art",
    "library.cat.poetry": "Poetry",
    "library.cat.food": "Food",
    "library.cat.fashion": "Fashion",
    "library.cat.family": "Family",
    "library.cat.podcast": "Podcast",
    "library.cat.radio": "Radio",
    "library.cat.ai": "Ai",
    "library.cat.artificial-intelligence": "Artificial Intelligence",
    "library.cat.song": "Song",
    "library.cat.ringtone": "Ringtone",
    "library.cat.web-series": "Web-series",
    "library.cat.idol": "Idol",
    "library.cat.dub": "Dub",
    "library.cat.80s": "80s",
    "library.cat.indie-games": "Indie Games",
    "library.cat.game-show": "Game Show",
    "library.lang.japanese": "Japanese",
    "library.lang.japenese": "Japenese",
    "library.lang.korean": "Korean",
    "library.lang.spanish": "Spanish",
    "library.lang.english": "English",
    "library.lang.french": "French",
    "library.lang.italian": "Italian",
    "library.lang.german": "German",
    "library.lang.deutsch": "Deutsch",
    "library.lang.russian": "Russian",
    "library.lang.hebrew": "Hebrew",
    "library.lang.chinese": "Chinese",
    "library.lang.arabic": "Arabic",
    "library.lang.swedish": "Swedish",
    "library.lang.polish": "Polish",
    "library.lang.turkish": "Turkish",
    "library.lang.finnish": "Finnish",
    "library.lang.greek": "Greek",
    "library.lang.indonesian": "Indonesian",
    "library.lang.thai": "Thai",
    "library.lang.hungarian": "Hungarian",
    "library.lang.filipino": "Filipino",
    "library.lang.malaysian": "Malaysian",
    "library.lang.portuguese": "Portuguese",
    "library.lang.brasileiro": "Brasileiro",
    "library.lang.pt-br": "Pt Br",
    "library.lang.romanian": "Romanian",
    "library.lang.hindi": "Hindi",
    "library.lang.dutch": "Dutch",
    "library.lang.czech": "Czech",
    "library.lang.vietnamese": "Vietnamese",
  },
};
